import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, increment, doc, updateDoc } from 'firebase/firestore';

const PORTALS = [
  { name: 'ЕРКНМ', icon: '📋', url: 'https://private.proverki.gov.ru' },
  { name: 'ПГС', icon: '🏛️', url: 'https://auth.pgs.gosuslugi.ru' },
  { name: 'ГИС ЖКХ', icon: '🏢', url: 'https://dom.gosuslugi.ru/#!/main' },
  { name: 'МП Инспектор', icon: '👮', url: 'https://mp.knd.gov.ru' },
];

const FGIS = () => {
  const currentUser = useSelector((state) => state.auth.user);
  const [middleUsers, setMiddleUsers] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [shareLink, setShareLink] = useState(''); // Поле пустое по умолчанию, чтобы пользователь вставил свою ссылку

  useEffect(() => {
    const q = query(collection(db, 'users'), where('role', '==', 'middle'));
    const unsubscribe = onSnapshot(q, (snap) => {
      setMiddleUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => console.error('Ошибка загрузки Мидл&Boss:', error));
    return () => unsubscribe();
  }, []);

  const toggleUser = (uid) => {
    setSelectedUserIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  // Чтение из буфера обмена
  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setShareLink(text);
    } catch (err) {
      alert('Не удалось прочитать буфер обмена. Пожалуйста, вставьте ссылку вручную (Ctrl+V).');
    }
  };

  // Отправка в личные сообщения
  const sendToChat = async () => {
    if (selectedUserIds.length === 0 || !shareLink.trim()) {
      alert('Выберите пользователей и вставьте ссылку');
      return;
    }
    setSending(true);
    const messageText = `📋 ${currentUser?.displayName || currentUser?.email?.split('@')[0]} направил(а) на подписание паспорт проверки\n🕐 ${new Date().toLocaleString('ru-RU')}`;

    try {
      for (const uid of selectedUserIds) {
        if (uid === currentUser?.uid) continue;
        const chatId = [currentUser.uid, uid].sort().join('_');
        await addDoc(collection(db, 'chats', chatId, 'messages'), {
          type: 'text',
          text: messageText,
          link: shareLink.trim(),
          from: currentUser.uid,
          fromEmail: currentUser.email,
          fromName: currentUser.displayName || currentUser.email,
          to: uid,
          read: false,
          createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'users', uid), { [`unreadFrom.${currentUser.uid}`]: increment(1) });
      }
      alert(`Ссылка отправлена в ЛС ${selectedUserIds.length} пользователю(ям)`);
      setShowSendModal(false);
      setSelectedUserIds([]);
      setShareLink('');
    } catch (error) {
      console.error('Ошибка отправки:', error);
      alert('Не удалось отправить сообщение');
    } finally {
      setSending(false);
    }
  };

  // Отправка на Email
  const sendToEmail = () => {
    if (selectedUserIds.length === 0 || !shareLink.trim()) {
      alert('Выберите пользователей и вставьте ссылку');
      return;
    }
    const selected = middleUsers.filter((u) => selectedUserIds.includes(u.id));
    const emails = selected.map((u) => u.email).filter(Boolean).join(',');

    if (!emails) {
      alert('У выбранных пользователей не указаны email-адреса в профиле');
      return;
    }

    const subject = encodeURIComponent('Направление на подписание паспорта проверки');
    const bodyText = `Коллеги, добрый день!\n\n${currentUser?.displayName || currentUser?.email?.split('@')[0]} направил(а) на подписание паспорт проверки.\n\n🕐 Дата и время: ${new Date().toLocaleString('ru-RU')}\n\n🔗 Ссылка для перехода:\n${shareLink.trim()}\n\nС уважением,\nГЖИ Санкт-Петербурга`;
    const body = encodeURIComponent(bodyText);

    // Открываем почтовый клиент с заполненными полями
    window.location.href = `mailto:${emails}?subject=${subject}&body=${body}`;
    
    setShowSendModal(false);
    setSelectedUserIds([]);
    setShareLink('');
  };

  const sidebarStyle = {
    width: '260px',
    background: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRight: '1px solid rgba(255,255,255,0.4)',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  };

  const btnStyle = {
    padding: '12px 16px',
    background: '#2a5298',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '14px',
    textAlign: 'center',
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
      {/* ЛЕВАЯ ПАНЕЛЬ */}
      <div style={sidebarStyle}>
        <h3 style={{ margin: '0 0 20px 0', color: '#1e3c72', fontSize: '18px' }}>📋 ФГИС</h3>
        <button onClick={() => setShowSendModal(true)} style={btnStyle}>
          ✉️ Отправить ссылку
        </button>
      </div>

      {/* ОСНОВНАЯ ОБЛАСТЬ: 4 карточки */}
      <div style={{ flex: 1, padding: '30px', overflowY: 'auto', background: '#f8f9fa' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
          {PORTALS.map((portal) => (
            <div key={portal.name} style={{
              background: '#fff', borderRadius: '12px', padding: '25px 20px',
              textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              border: '1px solid rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: '15px'
            }}>
              <div style={{ fontSize: '48px' }}>{portal.icon}</div>
              <h4 style={{ margin: 0, color: '#1e3c72', fontSize: '16px' }}>{portal.name}</h4>
              <button
                onClick={() => window.open(portal.url, '_blank')}
                style={{
                  padding: '10px 20px', background: '#4caf50', color: '#fff',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontWeight: 700, fontSize: '14px', width: '100%'
                }}
              >
                Открыть ↗
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* МОДАЛЬНОЕ ОКНО */}
      {showSendModal && (
        <div onClick={() => setShowSendModal(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'rgba(255,255,255,0.98)', borderRadius: '15px', padding: '25px',
            width: '90%', maxWidth: '500px', boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1e3c72' }}>✉️ Отправить ссылку</h3>
              <button onClick={() => setShowSendModal(false)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888' }}>✕</button>
            </div>

            {/* ПОЛЕ ДЛЯ ССЫЛКИ С КНОПКОЙ ВСТАВКИ */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontSize: '12px', color: '#666', fontWeight: 600, display: 'block', marginBottom: '5px' }}>
                Ссылка для отправки:
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={shareLink}
                  onChange={(e) => setShareLink(e.target.value)}
                  placeholder="Скопируйте и вставьте ссылку сюда..."
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
                />
                <button
                  onClick={pasteFromClipboard}
                  title="Вставить из буфера обмена"
                  style={{ padding: '8px 12px', background: '#f0f2f5', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '16px' }}
                >
                  📋
                </button>
              </div>
            </div>

            <div style={{ maxHeight: '250px', overflowY: 'auto', marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
              {middleUsers.length === 0 ? (
                <p style={{ color: '#888', textAlign: 'center', margin: '20px 0' }}>Пользователи Мидл&Boss не найдены</p>
              ) : (
                middleUsers.map((u) => (
                  <label key={u.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '8px',
                    borderRadius: '8px', cursor: 'pointer', background: selectedUserIds.includes(u.id) ? '#e3f2fd' : 'transparent', marginBottom: '4px',
                  }}>
                    <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => toggleUser(u.id)} style={{ width: '16px', height: '16px' }} />
                    <span style={{ fontSize: '13px' }}>
                      {u.displayName || u.email?.split('@')[0]}
                      <span style={{ color: '#999', fontSize: '11px' }}> · {u.email}</span>
                    </span>
                  </label>
                ))
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={sendToChat} disabled={selectedUserIds.length === 0 || !shareLink.trim() || sending} style={{
                flex: 1, padding: '12px', background: selectedUserIds.length === 0 || !shareLink.trim() || sending ? '#ccc' : '#2a5298',
                color: '#fff', border: 'none', borderRadius: '8px', cursor: selectedUserIds.length === 0 || !shareLink.trim() || sending ? 'not-allowed' : 'pointer', fontWeight: 700,
              }}>
                {sending ? '...' : `📩 В ЛС (${selectedUserIds.length})`}
              </button>
              <button onClick={sendToEmail} disabled={selectedUserIds.length === 0 || !shareLink.trim()} style={{
                flex: 1, padding: '12px', background: selectedUserIds.length === 0 || !shareLink.trim() ? '#ccc' : '#4caf50',
                color: '#fff', border: 'none', borderRadius: '8px', cursor: selectedUserIds.length === 0 || !shareLink.trim() ? 'not-allowed' : 'pointer', fontWeight: 700,
              }}>
                📧 На Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FGIS;